import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { apiService, setRefreshToken, getRefreshToken } from "../services/api";
import { toast } from "sonner";
import { User, AuthResponse, OTPResponse } from "../types";

// Use the User interface from types

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    phoneNumber: string
  ) => Promise<{ success: boolean; message: string }>;
  verifyOTP: (
    phoneNumber: string,
    otp: string
  ) => Promise<{ success: boolean; message: string; user?: User }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await apiService.get("/auth/profile/");
      if (response.data?.success && response.data?.user) {
        setUser(response.data.user);
      } else if (response.data?.user) {
        setUser(response.data.user);
      }
    } catch (error) {
      // Cookie absent or expired — user is not authenticated
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (
    phoneNumber: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiService.post<OTPResponse>("/auth/send-otp/", {
        phone_number: phoneNumber,
      });

      const data = response.data;
      return {
        success: data.success,
        message:
          data.message ||
          (data.success ? "OTP sent successfully" : "Failed to send OTP"),
      };
    } catch (error: any) {
      console.error("Login error:", error);
      const errorData = error.response?.data;
      return {
        success: false,
        message: errorData?.message || errorData?.error || "Failed to send OTP",
      };
    }
  };

  const verifyOTP = async (
    phoneNumber: string,
    otp: string
  ): Promise<{ success: boolean; message: string; user?: User }> => {
    console.log("🔐 [AuthContext] Verifying OTP for:", phoneNumber);
    try {
      const response = await apiService.post<AuthResponse>("/auth/verify/", {
        phone_number: phoneNumber,
        otp_code: otp,
      });

      const data = response.data;
      if (data.success && data.access_token && data.refresh_token) {
        console.log("✅ [AuthContext] OTP verified successfully");
        console.log("👤 [AuthContext] User status:", {
          google_signin_completed: data.user.google_signin_completed,
          empanelment_completed: data.user.empanelment_completed,
        });

        // access_token is set as HTTP-only cookie by the backend — nothing to store here
        setRefreshToken(data.refresh_token);
        setUser(data.user);

        return {
          success: true,
          message: data.message || "Login successful",
          user: data.user,
        };
      } else {
        console.warn("⚠️ [AuthContext] OTP verification failed:", data.message);
        return { success: false, message: data.message || "Invalid OTP" };
      }
    } catch (error: any) {
      console.error("❌ [AuthContext] OTP verification error:", error);
      const errorData = error.response?.data;
      return {
        success: false,
        message: errorData?.message || errorData?.error || "Invalid OTP",
      };
    }
  };

  const logout = () => {
    const refreshToken = getRefreshToken();
    setRefreshToken(null);
    setUser(null);
    toast.success("Logged out successfully");

    if (refreshToken) {
      apiService.post("/auth/logout/", {
        refresh_token: refreshToken
      }).catch((error) => {
        console.warn("Logout API call failed:", error);
      });
    }
  };

  const updateUser = (updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const refreshUser = async () => {
    try {
      const response = await apiService.get("/auth/profile/");
      if (response.data?.success && response.data?.user) {
        setUser(response.data.user);
      } else if (response.data?.user) {
        // Handle direct user response format
        setUser(response.data.user);
      }
    } catch (error) {
      console.error("Failed to refresh user:", error);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    verifyOTP,
    logout,
    refreshUser,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
